import React, { useState, useMemo, useEffect } from 'react';
import {
  Settings, Users, Zap, BarChart3, ShieldCheck, 
  ChevronRight, ChevronLeft, Download, Plus, Minus, 
  Check, Info, TrendingUp, AlertCircle, LayoutDashboard,
  FileText, Briefcase, Edit3, Search, Activity, Github,
  CheckCircle2, Clock, Terminal, PieChart, ExternalLink,
  Layers, Filter, MousePointer2, Target, Award, Code2, Cpu,
  BarChart, Calendar, Gauge, Rocket, Brain, ArrowLeft,
  MoreHorizontal
} from 'lucide-react';

const COSTS = { pm: 2500, lead: 7500, senior: 5500, qa: 4500 };

const Rass = () => {
  // Navigation State: 'dashboard' | 'planner' | 'control' | 'team'
  const [view, setView] = useState('dashboard');
  const [selectedProject, setSelectedProject] = useState(null);

  // Mock Data per Progetti Attivi
  const activeProjects = [
    { id: 'RaaS-2024-001', name: 'E-commerce Scaling', type: 'mvp', status: 'In Corso', health: 'Ottima', cost: 18500 },
    { id: 'RaaS-2024-002', name: 'Legacy Modernization', type: 'mig', status: 'In Corso', health: 'Stabile', cost: 24000 },
    { id: 'RaaS-2024-003', name: 'Mobile App Discovery', type: 'mvp', status: 'In Pausa', health: 'Attenzione', cost: 12500 }
  ];

  // Planner States
  const [step, setStep] = useState(1);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const [projectType, setProjectType] = useState('mvp');
  const [painLevels, setPainLevels] = useState({ backlog: 5, peaks: 4, hiring: 7 });
  
  // Team State
  const [team, setTeam] = useState({ pm: 1, lead: 1, senior: 2, qa: 1 });
  
  // State for Senior Developer Customization
  const [seniorSpecs, setSeniorSpecs] = useState([
    { id: 0, roleType: 'Frontend', language: 'React / Next.js' },
    { id: 1, roleType: 'Backend', language: 'Node.js / TS' }
  ]);

  // Sync seniorSpecs with team.senior count
  useEffect(() => {
    setSeniorSpecs((prevSpecs) => {
      if (prevSpecs.length < team.senior) {
        const diff = team.senior - prevSpecs.length;
        const newSpecs = Array.from({ length: diff }).map((_, i) => ({
          id: prevSpecs.length + i,
          roleType: 'Fullstack',
          language: 'Node.js / TS'
        }));
        return [...prevSpecs, ...newSpecs];
      }

      if (prevSpecs.length > team.senior) {
        return prevSpecs.slice(0, team.senior);
      }

      return prevSpecs;
    });
  }, [team.senior]);

  const updateSeniorSpec = (index, field, value) => {
    const updated = [...seniorSpecs];
    updated[index][field] = value;
    setSeniorSpecs(updated);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setProjectType(project.type);
    setView('control'); // Entra direttamente in control per progetti esistenti
  };

  const monthlyBaseCost = useMemo(() => {
    return (team.pm * COSTS.pm) + (team.lead * COSTS.lead) +
           (team.senior * COSTS.senior) + (team.qa * COSTS.qa);
  }, [team]);

  const monthlyCost = useMemo(() => {
    const avgPain = (painLevels.backlog + painLevels.peaks + painLevels.hiring) / 3;
    const impact = avgPain > 7 ? Math.round(monthlyBaseCost * 0.12) : 0;
    return monthlyBaseCost + impact;
  }, [painLevels, monthlyBaseCost]);

  // --- COMPONENT: PAIN SLIDER ---
  const PainSlider = ({ label, value, field, icon: Icon, color }) => (
    <div className="bg-slate-50 border border-slate-100 p-6 rounded-3xl space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${color} bg-white shadow-sm`}>
            <Icon size={18} />
          </div>
          <span className="font-bold text-slate-700">{label}</span>
        </div>
        <span className="font-mono font-black text-xl text-slate-900">{value}</span>
      </div>
      <div className="relative h-2 w-full bg-slate-200 rounded-lg overflow-hidden">
        <div 
          className={`absolute top-0 left-0 h-full transition-all duration-500 ${value > 7 ? 'bg-orange-500' : 'bg-blue-500'}`}
          style={{ width: `${(value / 10) * 100}%` }}
        />
      </div>
      <input 
        type="range" min="1" max="10" value={value} 
        onChange={(e) => setPainLevels({...painLevels, [field]: parseInt(e.target.value)})}
        className="w-full h-2 -mt-4 opacity-0 cursor-pointer z-10 relative"
      />
      <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
        <span>Gestibile</span>
        <span>Critico</span>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="max-w-[1440px] w-full mx-auto p-4 md:p-8 animate-in fade-in duration-500">
      <div className="flex justify-between items-end mb-10">
        <div>
          <h2 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Portfolio Progetti</h2>
          <p className="text-slate-500 italic">Panoramica di tutti i flussi RaaS attivi.</p>
        </div>
        <button 
          onClick={() => { setSelectedProject(null); setView('planner'); setStep(1); }}
          className="h-14 px-8 bg-orange-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex items-center gap-3 hover:bg-orange-700 transition-all shadow-xl shadow-orange-100"
        >
          <Plus size={20}/> Avvia Nuovo Progetto
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activeProjects.map((p) => (
          <div 
            key={p.id}
            onClick={() => handleProjectSelect(p)}
            className="bg-white border border-slate-200 rounded-[40px] p-8 hover:border-orange-500 transition-all group cursor-pointer shadow-sm hover:shadow-xl hover:shadow-slate-200/50"
          >
            <div className="flex justify-between items-start mb-6">
              <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-orange-50 group-hover:text-orange-600 transition-colors">
                <Briefcase size={28} />
              </div>
              <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${p.health === 'Ottima' ? 'bg-emerald-100 text-emerald-600' : p.health === 'Stabile' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                {p.health}
              </div>
            </div>
            
            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter mb-1">{p.name}</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">ID: {p.id}</p>
            
            <div className="flex justify-between items-end pt-6 border-t border-slate-100">
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase">Investimento Mensile</p>
                <p className="text-lg font-mono font-black text-slate-900">€{p.cost.toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 bg-slate-900 text-white rounded-full flex items-center justify-center group-hover:translate-x-1 transition-transform">
                <ChevronRight size={20} />
              </div>
            </div>
          </div>
        ))}

        {/* Card "Vuota" per aggiunta */}
        <div 
          onClick={() => { setSelectedProject(null); setView('planner'); setStep(1); }}
          className="border-2 border-dashed border-slate-200 rounded-[40px] p-8 flex flex-col items-center justify-center gap-4 hover:border-orange-300 hover:bg-white transition-all group cursor-pointer"
        >
          <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 group-hover:scale-110 transition-transform">
            <Plus size={32} />
          </div>
          <p className="font-black text-slate-400 uppercase text-xs tracking-widest">Aggiungi Progetto</p>
        </div>
      </div>
    </div>
  );

  const renderPerformance = () => (
    <div className="max-w-[1440px] w-full mx-auto p-4 md:p-8 animate-in fade-in duration-500">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Team Performance</h2>
          <p className="text-slate-500 italic">Analisi dell'efficienza e della qualità dell'output.</p>
        </div>
        <div className="bg-white p-1 rounded-xl border border-slate-200 flex">
          <button className="px-4 py-2 bg-slate-100 rounded-lg text-[10px] font-black uppercase">Settimana</button>
          <button className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase">Mese</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Efficienza Team', val: '94%', icon: Gauge, color: 'text-emerald-500' },
          { label: 'Code Coverage', val: '82%', icon: ShieldCheck, color: 'text-blue-500' },
          { label: 'Velocity Avg', val: '42 pts', icon: Rocket, color: 'text-orange-500' },
          { label: 'Hiring Saves', val: '€12k', icon: Award, color: 'text-purple-500' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-[32px] border border-slate-200 shadow-sm flex flex-col gap-3">
            <stat.icon className={stat.color} size={24} />
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
              <p className="text-2xl font-mono font-black text-slate-900">{stat.val}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Grafico Velocity (Simulato) */}
          <div className="bg-white border border-slate-200 rounded-[40px] p-8 shadow-sm">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-8">Sprint Velocity Trend</h3>
            <div className="h-64 flex items-end justify-between gap-4">
              {[45, 52, 48, 61, 55, 68, 72].map((h, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-3 group">
                  <div className="w-full bg-slate-50 rounded-t-xl relative overflow-hidden flex items-end h-full">
                    <div 
                      className="w-full bg-orange-600 transition-all duration-1000 group-hover:bg-slate-900" 
                      style={{ height: `${h}%` }} 
                    />
                  </div>
                  <span className="text-[9px] font-bold text-slate-400">S{i+1}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-[40px] p-8 shadow-sm">
             <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 italic">Performance per Risorsa</h3>
             <div className="space-y-4">
               {seniorSpecs.map((s, i) => (
                 <div key={i} className="flex items-center justify-between p-4 border border-slate-100 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-xs">{i+1}</div>
                      <div>
                        <p className="text-xs font-black text-slate-800 uppercase tracking-tighter">{s.roleType}</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">{s.language}</p>
                      </div>
                    </div>
                    <div className="flex gap-8">
                      <div className="text-right">
                        <p className="text-[9px] font-bold text-slate-400 uppercase">Velocity</p>
                        <p className="text-sm font-mono font-black text-emerald-600">High</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[9px] font-bold text-slate-400 uppercase">PR Quality</p>
                        <p className="text-sm font-mono font-black text-blue-600">98%</p>
                      </div>
                    </div>
                 </div>
               ))}
             </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="bg-white border border-slate-200 rounded-[40px] p-8 shadow-sm">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">AI Insights</h3>
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 rounded-2xl border border-orange-100">
                <div className="flex items-center gap-2 mb-2 text-orange-600">
                  <Brain size={16} />
                  <span className="text-[10px] font-black uppercase">Ottimizzazione</span>
                </div>
                <p className="text-xs text-slate-700 leading-relaxed font-bold italic">
                  "Il team {seniorSpecs[0]?.roleType} sta performando sopra la media. Considera di scalare i task di architettura per massimizzare l'output."
                </p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                <div className="flex items-center gap-2 mb-2 text-emerald-600">
                  <CheckCircle2 size={16} />
                  <span className="text-[10px] font-black uppercase">Stato</span>
                </div>
                <p className="text-xs text-slate-700 leading-relaxed font-bold italic">
                  "Sincronizzazione Lead/Senior ottimale. Riduzione del 15% nei bug segnalati questa settimana."
                </p>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 rounded-[40px] p-8 text-white relative overflow-hidden">
             <PieChart className="absolute -bottom-8 -right-8 opacity-10 w-40 h-40" />
             <h3 className="text-[10px] font-black opacity-50 uppercase tracking-widest mb-6">Allocation Mix</h3>
             <div className="space-y-4">
                <div className="flex justify-between items-center text-[10px] font-bold">
                  <span className="opacity-70 uppercase">Features Development</span>
                  <span>65%</span>
                </div>
                <div className="h-1.5 w-full bg-white/10 rounded-full">
                  <div className="bg-emerald-500 h-full w-[65%]" />
                </div>
                
                <div className="flex justify-between items-center text-[10px] font-bold pt-2">
                  <span className="opacity-70 uppercase">Bug Fixing</span>
                  <span>15%</span>
                </div>
                <div className="h-1.5 w-full bg-white/10 rounded-full">
                  <div className="bg-orange-500 h-full w-[15%]" />
                </div>

                <div className="flex justify-between items-center text-[10px] font-bold pt-2">
                  <span className="opacity-70 uppercase">Code Refactoring</span>
                  <span>20%</span>
                </div>
                <div className="h-1.5 w-full bg-white/10 rounded-full">
                  <div className="bg-blue-500 h-full w-[20%]" />
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderControl = () => (
    <div className="max-w-[1440px] w-full mx-auto p-4 md:p-8 animate-in fade-in duration-500">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Project Control</h2>
          <p className="text-slate-500 italic">Monitoraggio real-time del progetto attivo.</p>
        </div>
        <div className="flex gap-3">
          <button className="h-12 px-6 bg-white border border-slate-200 rounded-xl font-bold text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-slate-50 transition-all">
            <Download size={16}/> Export Report
          </button>
          <button onClick={() => setView('planner')} className="h-12 px-6 bg-slate-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-black transition-all shadow-lg shadow-slate-200">
            <Plus size={16}/> Nuovo Progetto
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Stats Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Card Progetto Principale */}
          <div className="bg-white border border-slate-200 rounded-[40px] p-8 shadow-sm">
            <div className="flex justify-between items-start mb-8">
              <div className="flex gap-5 items-center">
                <div className="w-16 h-16 bg-orange-600 rounded-[24px] flex items-center justify-center text-white shadow-xl shadow-orange-100">
                  <Zap size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">Progetto {projectType.toUpperCase()}</h3>
                  <div className="flex gap-2 mt-1">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-600 text-[10px] font-black uppercase rounded-full">In Corso</span>
                    <span className="px-3 py-1 bg-slate-100 text-slate-500 text-[10px] font-black uppercase rounded-full">ID: {selectedProject?.id || 'RaaS-2024-001'}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black text-slate-400 uppercase">Salute Progetto</p>
                <p className="text-2xl font-black text-emerald-500">{selectedProject?.health?.toUpperCase() || 'OTTIMA'}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-slate-100">
              <div className="space-y-2">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Activity size={12}/> Velocità Task
                </p>
                <p className="text-2xl font-mono font-black text-slate-900">12.5 <span className="text-xs font-bold text-slate-400">pts/sett</span></p>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <CheckCircle2 size={12}/> Completamento
                </p>
                <div className="flex items-end gap-2">
                  <p className="text-2xl font-mono font-black text-slate-900">45%</p>
                  <div className="w-full bg-slate-100 h-2 rounded-full mb-2 overflow-hidden">
                    <div className="bg-orange-600 h-full w-[45%]" />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Calendar size={12}/> Prossima Milestone
                </p>
                <p className="text-lg font-bold text-slate-900">UAT Release <span className="text-[10px] font-black text-orange-600 bg-orange-50 px-2 py-1 rounded-md ml-2">-12gg</span></p>
              </div>
            </div>
          </div>

          {/* Team Allocato */}
          <div className="bg-white border border-slate-200 rounded-[40px] p-8 shadow-sm">
             <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
               <Users size={16}/> Team Allocato & Competenze
             </h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               {Object.entries(team).map(([role, qty]) => qty > 0 && (
                 <div key={role} className="p-5 border border-slate-100 rounded-3xl bg-slate-50 flex items-center justify-between">
                   <div className="flex items-center gap-4">
                     <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-slate-400 shadow-sm">
                       {role === 'pm' ? <Briefcase size={20}/> : role === 'lead' ? <Zap size={20}/> : role === 'senior' ? <ShieldCheck size={20}/> : <Check size={20}/>}
                     </div>
                     <div>
                        <p className="font-black text-slate-800 uppercase text-xs tracking-tighter">{role === 'pm' ? 'Project Manager' : role === 'lead' ? 'Tech Lead' : role === 'senior' ? 'Senior Developer' : 'QA Engineer'}</p>
                        <p className="text-[10px] font-bold text-slate-400">Allocazione 100%</p>
                     </div>
                   </div>
                   <span className="text-xl font-mono font-black text-slate-900">x{qty}</span>
                 </div>
               ))}
             </div>
             
             {seniorSpecs.length > 0 && (
               <div className="mt-6 space-y-3">
                 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Dettaglio Senior Developer</p>
                 <div className="flex flex-wrap gap-2">
                    {seniorSpecs.map((s, i) => (
                      <div key={i} className="px-4 py-2 bg-white border border-slate-200 rounded-2xl flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                        <span className="text-xs font-bold text-slate-700">{s.roleType}: <span className="text-orange-600 font-black">{s.language}</span></span>
                      </div>
                    ))}
                 </div>
               </div>
             )}
          </div>
        </div>

        {/* Sidebar Cost Analysis */}
        <div className="space-y-8">
          <div className="bg-slate-900 text-white rounded-[40px] p-8 shadow-xl shadow-slate-200 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-600/20 blur-3xl rounded-full -mr-16 -mt-16" />
            <h3 className="text-[10px] font-black opacity-50 uppercase tracking-widest mb-6">Financial Overview</h3>
            
            <div className="space-y-6">
              <div>
                <p className="text-xs font-bold opacity-70 mb-1">Costo Mensile Corrente</p>
                <p className="text-4xl font-mono font-black">€{monthlyCost.toLocaleString()}</p>
              </div>
              
              <div className="pt-6 border-t border-white/10 space-y-4">
                <div className="flex justify-between items-center text-xs">
                  <span className="opacity-60">Utilizzo Budget</span>
                  <span className="font-bold">78%</span>
                </div>
                <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                  <div className="bg-orange-500 h-full w-[78%]" />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-[10px] font-bold">
                  <span className="opacity-50">GOVERNANCE & PMO</span>
                  <span>INCLUSO</span>
                </div>
                <div className="flex justify-between text-[10px] font-bold">
                  <span className="opacity-50">INFRASTRUCTURE FEE</span>
                  <span>INCLUSO</span>
                </div>
              </div>

              <button className="w-full h-14 bg-white text-slate-900 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-orange-500 hover:text-white transition-all shadow-lg shadow-black/20">
                Gestisci Fatturazione
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-[40px] p-8 shadow-sm">
             <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">Attività Recenti</h3>
             <div className="space-y-6">
               {[
                 { time: '2h fa', event: 'Sprint Review completata', icon: Github },
                 { time: '4h fa', event: 'Nuova risorsa QA allocata', icon: Users },
                 { time: 'Ieri', event: 'Milestone Alpha raggiunta', icon: Target }
               ].map((item, i) => (
                 <div key={i} className="flex gap-4">
                   <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 border border-slate-100 flex-shrink-0">
                     <item.icon size={18} />
                   </div>
                   <div>
                     <p className="text-sm font-bold text-slate-800">{item.event}</p>
                     <p className="text-[10px] font-bold text-slate-400 uppercase">{item.time}</p>
                   </div>
                 </div>
               ))}
             </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlanner = () => (
    <div className="flex flex-col lg:flex-row max-w-[1440px] w-full mx-auto p-4 md:p-8 gap-8 animate-in fade-in duration-500">
      {/* SIDEBAR SINISTRA */}
      <div className="lg:w-80 flex-shrink-0">
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm sticky top-28">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">Configurazione</h3>
          <div className="space-y-4">
            {[1, 2, 3, 4].map(n => (
              <button key={n} disabled={n > maxStepReached} onClick={() => setStep(n)} className={`w-full flex items-center gap-4 text-left transition-all ${n <= maxStepReached ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm font-bold ${step === n ? 'bg-orange-600 text-white ring-4 ring-orange-100' : 'bg-slate-100 text-slate-400'}`}>
                  {n}
                </div>
                <span className={`text-sm font-bold ${step === n ? 'text-slate-900' : 'text-slate-400'}`}>
                  {n === 1 ? 'Progetto' : n === 2 ? 'Analisi Pain' : n === 3 ? 'Team' : 'Review'}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-10 pt-10 border-t border-slate-100">
            <span className="text-[10px] font-bold text-slate-400 uppercase">Budget Mensile Stimato</span>
            <p className="text-3xl font-mono font-black text-orange-600">€{monthlyCost.toLocaleString()}</p>
            <p className="text-[10px] text-slate-400 mt-2 font-bold uppercase tracking-tighter">Include Governance & PMO</p>
          </div>
        </div>
      </div>

      {/* CONTENUTO DESTRA */}
      <div className="flex-1 bg-white rounded-[40px] p-6 md:p-12 border border-slate-200 shadow-sm min-h-[600px] flex flex-col relative overflow-hidden">
        {step === 1 && (
          <div className="animate-in fade-in duration-500">
            <h2 className="text-3xl font-black mb-2 uppercase tracking-tighter">Nuovo Progetto RaaS</h2>
            <p className="text-slate-500 mb-10">Seleziona il framework di ingaggio iniziale.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {id: 'mvp', title: 'MVP Discovery', icon: Zap, desc: 'Per validare nuove idee.'}, 
                {id: 'run', title: 'Steady Run', icon: Activity, desc: 'Mantenimento e scaling.'}, 
                {id: 'mig', title: 'Migration', icon: Layers, desc: 'Cloud e legacy modernizing.'}, 
                {id: 'sec', title: 'Security', icon: ShieldCheck, desc: 'Compliance e hardening.'}
              ].map(type => (
                <button key={type.id} onClick={() => setProjectType(type.id)} className={`p-8 rounded-[32px] border-2 text-left transition-all group ${projectType === type.id ? 'border-orange-500 bg-orange-50/50' : 'border-slate-100 hover:border-slate-300'}`}>
                  <type.icon size={32} className={`mb-4 ${projectType === type.id ? 'text-orange-600' : 'text-slate-400'}`} />
                  <h3 className="font-black text-xl uppercase tracking-tighter">{type.title}</h3>
                  <p className="text-sm text-slate-500 mt-2">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-in fade-in duration-500">
            <h2 className="text-3xl font-black mb-2 uppercase tracking-tighter">Analisi del Pain</h2>
            <p className="text-slate-500 mb-10">Definisci l'urgenza dei tuoi colli di bottiglia.</p>
            <div className="grid grid-cols-1 gap-6">
              <PainSlider label="Backlog Pressure" field="backlog" value={painLevels.backlog} icon={FileText} color="text-blue-500" />
              <PainSlider label="Peak Management" field="peaks" value={painLevels.peaks} icon={TrendingUp} color="text-emerald-500" />
              <PainSlider label="Hiring Difficulty" field="hiring" value={painLevels.hiring} icon={Search} color="text-purple-500" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-in fade-in duration-500 overflow-y-auto pb-10">
            <h2 className="text-3xl font-black mb-2 uppercase tracking-tighter">Componi il Core Team</h2>
            <p className="text-slate-500 mb-8">Definisci le figure chiave e personalizza le competenze dei Senior Dev.</p>
            
            <div className="space-y-6">
              {[
                { id: 'pm', name: 'Project Manager', icon: Briefcase },
                { id: 'lead', name: 'Tech Lead / Architect', icon: Zap },
                { id: 'senior', name: 'Senior Developer', icon: ShieldCheck },
                { id: 'qa', name: 'QA Engineer', icon: Check }
              ].map(role => (
                <div key={role.id} className="space-y-3">
                  <div className={`flex justify-between items-center p-6 border rounded-[28px] transition-all bg-slate-50 border-slate-100 hover:bg-white hover:shadow-lg`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm bg-white ${role.id === 'senior' && team.senior > 0 ? 'text-orange-600' : 'text-slate-400'}`}>
                        <role.icon size={20} />
                      </div>
                      <span className="font-bold uppercase tracking-tighter text-slate-800">{role.name}</span>
                    </div>
                    <div className="flex items-center gap-6">
                      <button onClick={() => setTeam({...team, [role.id]: Math.max(0, team[role.id]-1)})} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors">
                        <Minus size={18}/>
                      </button>
                      <span className="font-mono font-black text-2xl w-8 text-center text-slate-900">{team[role.id]}</span>
                      <button onClick={() => setTeam({...team, [role.id]: team[role.id]+1})} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors">
                        <Plus size={18}/>
                      </button>
                    </div>
                  </div>

                  {role.id === 'senior' && team.senior > 0 && (
                    <div className="space-y-2 pl-4 animate-in slide-in-from-top-2 duration-300">
                      {seniorSpecs.map((spec, idx) => (
                        <div key={idx} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex items-center gap-2 min-w-[120px]">
                            <Code2 size={16} className="text-orange-600" />
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Dev #{idx + 1}</span>
                          </div>
                          
                          <div className="flex-1 grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                              <label className="text-[9px] font-bold text-slate-400 uppercase whitespace-nowrap">Tipo:</label>
                              <select 
                                value={spec.roleType}
                                onChange={(e) => updateSeniorSpec(idx, 'roleType', e.target.value)}
                                className="flex-1 bg-slate-50 border border-slate-100 rounded-lg p-2 text-xs font-bold outline-none focus:ring-2 focus:ring-orange-500/20"
                              >
                                <option>Frontend</option>
                                <option>Backend</option>
                                <option>Fullstack</option>
                                <option>Mobile</option>
                                <option>DevOps</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-3">
                              <label className="text-[9px] font-bold text-slate-400 uppercase whitespace-nowrap">Tech:</label>
                              <select 
                                value={spec.language}
                                onChange={(e) => updateSeniorSpec(idx, 'language', e.target.value)}
                                className="flex-1 bg-slate-50 border border-slate-100 rounded-lg p-2 text-xs font-bold outline-none focus:ring-2 focus:ring-orange-500/20"
                              >
                                <option>React / Next.js</option>
                                <option>Node.js / TS</option>
                                <option>Java / Spring</option>
                                <option>Python / Django</option>
                                <option>C# / .NET</option>
                                <option>Flutter</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="text-center py-10 animate-in zoom-in duration-500 flex-1 flex flex-col justify-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-[32px] flex items-center justify-center text-emerald-600 mx-auto mb-8 shadow-xl shadow-emerald-50 ring-4 ring-white">
              <Check size={40} strokeWidth={3} />
            </div>
            <h2 className="text-4xl font-black mb-4 tracking-tighter uppercase">Proposta Pronta</h2>
            <div className="bg-slate-50 p-6 rounded-3xl max-w-md mx-auto mb-8 border border-slate-100">
               <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Riepilogo Competenze</h4>
               <div className="flex flex-wrap justify-center gap-2">
                 {seniorSpecs.map((s, i) => (
                   <span key={i} className="px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-slate-700">
                     {s.roleType}: {s.language}
                   </span>
                 ))}
               </div>
            </div>
            <button 
              onClick={() => {
                setSelectedProject({ id: 'RaaS-NEW', name: 'Nuovo Progetto', health: 'Ottima' });
                setView('control');
              }} 
              className="bg-orange-600 text-white px-12 py-5 rounded-[24px] font-black uppercase tracking-widest shadow-xl shadow-orange-100 hover:scale-105 transition-all mx-auto block"
            >
              Attiva Progetto & Monitora
            </button>
          </div>
        )}

        <div className="mt-auto pt-8 flex justify-between">
          <button onClick={() => setStep(s => Math.max(1, s-1))} className={`font-black text-slate-400 uppercase tracking-widest text-xs flex items-center gap-2 ${step === 1 ? 'invisible' : ''}`}><ChevronLeft size={16}/> Indietro</button>
          {step < 4 && <button onClick={() => {setStep(s => s+1); setMaxStepReached(m => Math.max(m, step+1))}} className="bg-slate-900 text-white px-10 py-4 rounded-[18px] font-black uppercase tracking-widest text-xs hover:bg-black transition-all">Avanti</button>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans">
      <header className="h-20 bg-white border-b border-slate-200 px-6 md:px-10 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <img src="https://www.helpsoftware.it/static-site/logo.jpeg" alt="Logo" className="h-10 object-contain" />
          <div className="border-l border-slate-200 pl-3">
            <h1 className="font-black text-lg leading-none text-slate-800 tracking-tighter italic uppercase">Helpsoftware</h1>
            <p className="text-[10px] uppercase font-bold text-orange-600 tracking-widest">Governance Ecosystem</p>
          </div>
        </div>

        {view !== 'dashboard' && (
          <nav className="hidden md:flex bg-slate-100 p-1 rounded-2xl">
            <button onClick={() => setView('planner')} className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${view === 'planner' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Planner</button>
            <button onClick={() => setView('control')} className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${view === 'control' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Control</button>
            <button onClick={() => setView('team')} className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${view === 'team' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Performance</button>
          </nav>
        )}

        <div className="flex items-center gap-4">
          {view !== 'dashboard' && (
            <button 
              onClick={() => setView('dashboard')}
              className="hidden lg:flex items-center gap-2 px-4 py-2 text-slate-500 hover:text-slate-900 font-black uppercase text-[10px] tracking-widest transition-colors"
            >
              <ArrowLeft size={16} /> Home Progetti
            </button>
          )}
          <div className="w-10 h-10 rounded-2xl bg-orange-100 flex items-center justify-center text-orange-600 border-2 border-white shadow-sm font-black italic">JD</div>
        </div>
      </header>

      <main className="flex-1">
        {view === 'dashboard' && renderDashboard()}
        {view === 'planner' && renderPlanner()}
        {view === 'control' && renderControl()}
        {view === 'team' && renderPerformance()}
      </main>
    </div>
  );
};

export default Rass;
